/**
 * **Molinete ZKTeco** — contrato compartido entre backend, portal y el agente de Windows
 * (`viva-molinete.exe`). Decisión: `docs/decisiones/molinete-zkteco.md`.
 *
 * El terminal ZKTeco del molinete (F21 / ProCapture-T, algoritmo ZKFinger v10) SIGUE
 * haciendo el match de la huella y accionando el relé: funciona sin internet y conserva
 * las huellas. El sistema le sincroniza el padrón (alta con PIN = número de socio,
 * bloqueo/desbloqueo según RN-ACC-08), recibe los ingresos y puede abrir desde recepción.
 *
 * Dos canales, elegidos por equipo en el lugar:
 * - `push` (ADMS / «Servidor en la nube»): el terminal llama a `/iclock/*` del backend.
 * - `agent`: el `access-agent` corre en la PC del gimnasio y habla TCP 4370 con el
 *   terminal en la LAN. Usa los endpoints `/api/access/zk/agent/*` de este contrato.
 *
 * Los comandos son **neutros de canal** (`ZkCommandKind`): el backend los encola una sola
 * vez y cada canal los traduce a su forma (texto ADMS o llamada TCP).
 */
import { z } from "zod";
import { objectId, reasonRequired } from "./schemas/base.js";
import { FINGERS } from "./schemas/acceso.js";
export const ZK_CHANNELS = ["push", "agent"];
/**
 * Cómo se bloquea a un socio en el equipo:
 * - `disable`: el usuario queda en el terminal (con sus huellas) pero deshabilitado.
 * - `delete`: se lo borra del terminal; al habilitarlo se lo vuelve a cargar con sus
 *   plantillas desde el almacén cifrado. Sólo se borra si el almacén tiene sus plantillas.
 */
export const ZK_BLOCK_MODES = ["disable", "delete"];
export const ZK_DEVICE_STATUSES = ["active", "retired"];
export const ZK_COMMAND_KINDS = [
    /** Alta o actualización del usuario (PIN, nombre, habilitado). */
    "user_upsert",
    /** Baja del usuario del equipo (con `blockMode: delete`). */
    "user_delete",
    /** Carga de una plantilla (huella) de un usuario. */
    "template_upsert",
    /** Lectura de los usuarios del equipo (sin plantillas). */
    "query_users",
    /** Lectura de las plantillas del equipo (y de sus usuarios). */
    "query_templates",
    /** Pedirle al equipo que registre la huella de un usuario en ese momento. */
    "enroll",
    /** Abrir el molinete (pulso del relé). */
    "open",
    /** Pedir la info del equipo: modelo, firmware, cantidades. */
    "info",
];
/** Comandos que LEEN el equipo: no necesitan backup previo. */
export const ZK_READ_COMMANDS = [
    "query_users",
    "query_templates",
    "info",
    "open",
];
/**
 * Comandos que ESCRIBEN el equipo (alta, bloqueo, baja, huellas, registro de huella): todo
 * lo que no es lectura ni apertura. Son los que frenan el backup y el modo de escritura.
 */
export const ZK_WRITE_COMMANDS = ZK_COMMAND_KINDS.filter((kind) => !ZK_READ_COMMANDS.includes(kind));
/**
 * Modo de escritura del equipo — el freno para instalar al lado de Control Gym sin romperlo:
 * - `off`: sólo lee y abre. Ninguna escritura sale, ni para los socios de prueba.
 * - `shadow` («sombra», el default de todo equipo): sólo salen las escrituras de los socios
 *   de prueba (`writeAllowlist`); el resto queda `shadowed`, a la vista como «lo que haría».
 * - `on`: escribe todo lo que el padrón pide.
 * Las lecturas y la apertura salen en cualquier modo.
 */
export const ZK_WRITE_MODES = ["off", "shadow", "on"];
export const ZK_DEFAULT_WRITE_MODE = "shadow";
/** Tope de socios de prueba por equipo: es una lista de prueba, no un padrón. */
export const ZK_WRITE_ALLOWLIST_MAX = 50;
/**
 * `shadowed` = escritura retenida por el modo de escritura: no se entregó ni se borró; es
 * «lo que haría» el sistema. Vuelve a `pending` si el modo o la lista de prueba la habilitan.
 */
export const ZK_COMMAND_STATUSES = ["pending", "sent", "ok", "error", "cancelled", "shadowed"];
/**
 * De dónde vino una plantilla: leída del equipo, traída de Control Gym, registrada en el
 * equipo desde la ficha, o capturada en recepción con el lector de la PC
 * (`viva-puente-huellas.exe`, que entrega la plantilla ZKFinger v10 del SDK).
 */
export const ZK_TEMPLATE_SOURCES = ["terminal", "controlgym", "enroll", "puente"];
/** Algoritmo de las plantillas que se guardan. Hoy, sólo ZKFinger v10. */
export const ZK_TEMPLATE_ALGORITHMS = ["zk_v10"];
/** Cómo se asoció un usuario del equipo a un socio. */
export const ZK_MATCH_KINDS = ["member_number", "user_map", "doc_number", "name", "manual"];
export const ZK_MATCH_STATUSES = ["matched", "unmatched", "discarded"];
/**
 * Índice de dedo de ZKTeco (FID 0–9): 0 = meñique izquierdo … 4 = pulgar izquierdo,
 * 5 = pulgar derecho … 9 = meñique derecho. Es la convención del SDK y del protocolo.
 */
export const ZK_FINGER_INDEX = {
    left_little: 0,
    left_ring: 1,
    left_middle: 2,
    left_index: 3,
    left_thumb: 4,
    right_thumb: 5,
    right_index: 6,
    right_middle: 7,
    right_ring: 8,
    right_little: 9,
};
export function zkFingerOf(fid) {
    const hit = Object.entries(ZK_FINGER_INDEX).find(([, index]) => index === fid);
    return hit ? hit[0] : null;
}
export const zkPin = z
    .string()
    .trim()
    .regex(/^[0-9]{1,14}$/, "El PIN del equipo son sólo números (hasta 14 dígitos)");
export const zkFid = z.number().int().min(0).max(9);
// ─────────────────────────────────────────────────────────────────────────────
// Agente de Windows ↔ backend (`/api/access/zk/agent/*`, `Authorization: ApiKey`)
// ─────────────────────────────────────────────────────────────────────────────
/** Un usuario tal como lo devuelve el equipo. */
export const zkDeviceUser = z.object({
    pin: zkPin,
    name: z.string().max(80).default(""),
    /** 0 = usuario, 14 = administrador. El bit 0 prendido = deshabilitado. */
    privilege: z.number().int().min(0).max(255).default(0),
    enabled: z.boolean().default(true),
    card: z.string().max(40).default(""),
    /** Número interno del equipo (uid). Sólo informativo. */
    uid: z.number().int().nullable().default(null),
});
/** Una plantilla tal como la devuelve el equipo. `template` = base64 de la plantilla. */
export const zkDeviceTemplate = z.object({
    pin: zkPin,
    fid: zkFid,
    template: z.string().min(16).max(20_000),
    valid: z.number().int().min(0).max(255).default(1),
});
/** Info del equipo. `null` = no se pudo leer (no es cero). */
export const zkDeviceInfo = z.object({
    model: z.string().max(80).nullable().default(null),
    firmware: z.string().max(120).nullable().default(null),
    serialNumber: z.string().max(60).nullable().default(null),
    platform: z.string().max(80).nullable().default(null),
    /** Versión del algoritmo de huella (`10` = ZKFinger v10). */
    fpAlgorithm: z.string().max(20).nullable().default(null),
    users: z.number().int().nullable().default(null),
    fingerprints: z.number().int().nullable().default(null),
    faces: z.number().int().nullable().default(null),
    records: z.number().int().nullable().default(null),
    ip: z.string().max(60).nullable().default(null),
    pushVersion: z.string().max(40).nullable().default(null),
});
/**
 * Comando para el agente. Lleva todo lo necesario para ejecutarlo sin volver a preguntar:
 * con `template_upsert` viaja la plantilla en claro **sólo hacia el canal del equipo**
 * (por HTTPS, a la PC que la escribe en el terminal), nunca hacia el portal.
 */
export const zkAgentCommand = z.discriminatedUnion("kind", [
    z.object({
        id: objectId,
        zkDeviceId: objectId,
        kind: z.literal("user_upsert"),
        params: z.object({ pin: zkPin, name: z.string(), enabled: z.boolean(), privilege: z.number().int() }),
    }),
    z.object({ id: objectId, zkDeviceId: objectId, kind: z.literal("user_delete"), params: z.object({ pin: zkPin }) }),
    z.object({
        id: objectId,
        zkDeviceId: objectId,
        kind: z.literal("template_upsert"),
        params: z.object({ pin: zkPin, fid: zkFid, template: z.string(), valid: z.number().int() }),
    }),
    z.object({
        id: objectId,
        zkDeviceId: objectId,
        kind: z.literal("query_users"),
        params: z.object({ pin: zkPin.nullable() }),
    }),
    z.object({
        id: objectId,
        zkDeviceId: objectId,
        kind: z.literal("query_templates"),
        params: z.object({ pin: zkPin.nullable() }),
    }),
    z.object({
        id: objectId,
        zkDeviceId: objectId,
        kind: z.literal("enroll"),
        params: z.object({ pin: zkPin, fid: zkFid }),
    }),
    z.object({
        id: objectId,
        zkDeviceId: objectId,
        kind: z.literal("open"),
        params: z.object({ seconds: z.number().int().min(1).max(60) }),
    }),
    z.object({ id: objectId, zkDeviceId: objectId, kind: z.literal("info"), params: z.object({}) }),
]);
/** Configuración de un equipo para el agente (incluye la Comm Key: es el canal del equipo). */
export const zkAgentDeviceConfig = z.object({
    id: objectId,
    name: z.string(),
    ip: z.string(),
    port: z.number().int(),
    /** Comm Key del equipo (0 si no tiene). */
    commKey: z.number().int().min(0),
    serialNumber: z.string().nullable(),
    /**
     * Desde cuándo pedir marcaciones: la última recibida, en hora LOCAL del equipo
     * (`YYYY-MM-DD HH:mm:ss`). `null` = el sistema todavía no recibió ninguna.
     */
    punchesSince: z.string().nullable(),
});
/** `GET /api/access/zk/agent/poll` */
export const zkAgentPollResponse = z.object({
    serverTime: z.string(),
    devices: z.array(zkAgentDeviceConfig),
    /** Los comandos entregados quedan `sent`; el agente devuelve el resultado de cada uno. */
    commands: z.array(zkAgentCommand),
});
/** Intercambio crudo con el equipo, para el log crudo (las plantillas van redactadas). */
export const zkRawEntry = z.object({
    direction: z.enum(["to_device", "from_device"]),
    op: z.string().max(80),
    detail: z.string().max(8_000),
    at: z.string().optional(),
});
/** `POST /api/access/zk/agent/commands/:id/result` */
export const zkAgentCommandResultBody = z.object({
    ok: z.boolean(),
    /** Código que devolvió el equipo, si devolvió uno. */
    returnCode: z.number().int().nullable().default(null),
    error: z.string().max(2_000).nullable().default(null),
    data: z
        .object({
        users: z.array(zkDeviceUser).max(20_000).optional(),
        templates: z.array(zkDeviceTemplate).max(60_000).optional(),
        info: zkDeviceInfo.optional(),
    })
        .optional(),
    raw: z.array(zkRawEntry).max(200).default([]),
});
/** Una marcación (huella reconocida) tal como la guarda el equipo. */
export const zkPunch = z.object({
    pin: zkPin,
    /** Hora LOCAL del equipo, `YYYY-MM-DD HH:mm:ss` (el equipo no sabe de zonas horarias). */
    localTime: z.string().regex(/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}$/),
    /** Modo de verificación: 1 = huella, 0 = clave, 4 = tarjeta, 15 = rostro. */
    verify: z.number().int().nullable().default(null),
    status: z.number().int().nullable().default(null),
});
/** `POST /api/access/zk/agent/devices/:id/punches` */
export const zkAgentPunchesBody = z.object({
    punches: z.array(zkPunch).max(5_000),
    raw: z.array(zkRawEntry).max(200).default([]),
});
/** `POST /api/access/zk/agent/devices/:id/status`: el agente reporta si llega al equipo. */
export const zkAgentStatusBody = z.object({
    reachable: z.boolean(),
    error: z.string().max(2_000).nullable().default(null),
    info: zkDeviceInfo.optional(),
    raw: z.array(zkRawEntry).max(200).default([]),
});
/** Reporte del modo diagnóstico del `.exe` (también se guarda en el log crudo). */
export const zkDiagnosticReport = z.object({
    at: z.string(),
    host: z.string().max(120),
    scannedSubnets: z.array(z.string()).max(32),
    found: z
        .array(z.object({
        ip: z.string(),
        port: z.number().int(),
        connected: z.boolean(),
        commKeyOk: z.boolean().nullable(),
        info: zkDeviceInfo.nullable(),
        openTested: z.boolean(),
        openOk: z.boolean().nullable(),
        error: z.string().nullable(),
    }))
        .max(64),
    notes: z.array(z.string().max(500)).max(50),
});
/** `POST /api/access/zk/agent/diagnostics` */
export const zkAgentDiagnosticsBody = z.object({ report: zkDiagnosticReport });
/** Un socio/usuario leído de la base de Control Gym en la PC. */
export const zkControlgymUser = z.object({
    /** Número de usuario en el terminal (el PIN que Control Gym le cargó), si la base lo trae. */
    pin: zkPin.nullable(),
    docNumber: z.string().max(20).nullable(),
    name: z.string().max(120).default(""),
    memberNumber: z.string().max(20).nullable().default(null),
});
export const zkControlgymTemplate = z.object({
    pin: zkPin.nullable(),
    docNumber: z.string().max(20).nullable(),
    fid: zkFid,
    template: z.string().min(16).max(20_000),
});
/** `POST /api/access/zk/agent/controlgym`: lo que el modo `controlgym` encontró y exportó. */
export const zkAgentControlgymBody = z.object({
    report: z.object({
        host: z.string().max(120),
        databases: z
            .array(z.object({
            path: z.string().max(400),
            kind: z.enum(["access", "sqlite", "firebird", "sqlserver", "unknown"]),
            readable: z.boolean(),
            detail: z.string().max(1_000),
        }))
            .max(50),
    }),
    users: z.array(zkControlgymUser).max(20_000),
    templates: z.array(zkControlgymTemplate).max(60_000),
});
// ─────────────────────────────────────────────────────────────────────────────
// Portal (`/api/access/zk/*`, sesión de staff)
// ─────────────────────────────────────────────────────────────────────────────
export const zkDeviceCreateBody = z
    .object({
    name: z.string().trim().min(2).max(80),
    accessPointId: objectId,
    channel: z.enum(ZK_CHANNELS),
    /** Número de serie (Info del sistema). Obligatorio con `push`: es su credencial. */
    serialNumber: z.string().trim().min(4).max(60).nullable().default(null),
    ip: z.string().trim().max(60).nullable().default(null),
    port: z.number().int().min(1).max(65535).default(4370),
    /** Comm Key del equipo (sólo números). Vacío = sin clave. Se guarda cifrada. */
    commKey: z.string().trim().regex(/^\d{0,9}$/, "La Comm Key son sólo números").default(""),
    blockMode: z.enum(ZK_BLOCK_MODES).default("disable"),
    /** Con `agent`: el dispositivo `access_agent` (la PC) que lo maneja. */
    agentDeviceId: objectId.nullable().default(null),
    /** Todo equipo nace en «sombra»: no escribe hasta que alguien lo decide. */
    writeMode: z.enum(ZK_WRITE_MODES).default(ZK_DEFAULT_WRITE_MODE),
})
    .superRefine((value, ctx) => {
    if (value.channel === "push" && !value.serialNumber) {
        ctx.addIssue({ code: "custom", path: ["serialNumber"], message: "Con ADMS el número de serie es obligatorio" });
    }
    if (value.channel === "agent") {
        if (!value.ip)
            ctx.addIssue({ code: "custom", path: ["ip"], message: "Con el agente la IP es obligatoria" });
        if (!value.agentDeviceId) {
            ctx.addIssue({ code: "custom", path: ["agentDeviceId"], message: "Elegí la PC (agente) que maneja el equipo" });
        }
    }
});
export const zkDeviceUpdateBody = z.object({
    name: z.string().trim().min(2).max(80).optional(),
    channel: z.enum(ZK_CHANNELS).optional(),
    serialNumber: z.string().trim().min(4).max(60).nullable().optional(),
    ip: z.string().trim().max(60).nullable().optional(),
    port: z.number().int().min(1).max(65535).optional(),
    /** `undefined` = no se toca. `""` = sin clave. */
    commKey: z.string().trim().regex(/^\d{0,9}$/, "La Comm Key son sólo números").optional(),
    blockMode: z.enum(ZK_BLOCK_MODES).optional(),
    agentDeviceId: objectId.nullable().optional(),
});
export const zkRetireBody = z.object({ reason: reasonRequired });
/** `PATCH /api/access/zk/devices/:id/write-mode`: modo y/o socios de prueba. */
export const zkWriteModeBody = z
    .object({
    writeMode: z.enum(ZK_WRITE_MODES).optional(),
    /** Lista COMPLETA de socios de prueba (reemplaza la anterior). */
    writeAllowlist: z
        .array(objectId)
        .max(ZK_WRITE_ALLOWLIST_MAX, `Hasta ${ZK_WRITE_ALLOWLIST_MAX} socios de prueba por equipo`)
        .optional(),
})
    .refine((v) => v.writeMode !== undefined || v.writeAllowlist !== undefined, {
    message: "Indicá el modo de escritura o los socios de prueba",
});
export const zkShadowedQuery = z.object({
    limit: z.coerce.number().int().min(1).max(500).default(200),
});
/** «Abrir molinete»: por equipo o por punto de acceso (el semáforo conoce el punto). */
export const zkOpenBody = z
    .object({
    zkDeviceId: objectId.optional(),
    accessPointId: objectId.optional(),
    /** Por qué se abre a mano (opcional; queda en la auditoría). */
    note: z.string().trim().max(300).optional(),
})
    .refine((v) => Boolean(v.zkDeviceId || v.accessPointId), {
    message: "Indicá el equipo o el punto de acceso",
});
export const zkCommandsQuery = z.object({
    status: z.enum(ZK_COMMAND_STATUSES).optional(),
    limit: z.coerce.number().int().min(1).max(500).default(100),
});
export const zkRawLogsQuery = z.object({
    zkDeviceId: objectId.optional(),
    serialNumber: z.string().max(60).optional(),
    limit: z.coerce.number().int().min(1).max(500).default(100),
});
export const zkDeviceUsersQuery = z.object({
    match: z.enum(ZK_MATCH_STATUSES).optional(),
    limit: z.coerce.number().int().min(1).max(5_000).default(500),
});
export const zkAssignBody = z.object({ memberId: objectId });
export const zkDiscardBody = z.object({ reason: reasonRequired });
export const zkLoadTemplatesBody = z.object({
    /** Opcional: sólo los socios cuyas plantillas vinieron de este equipo (replicar entre sedes). */
    sourceDeviceId: objectId.nullable().default(null),
});
export const zkEnrollBody = z.object({
    zkDeviceId: objectId,
    finger: z.enum(FINGERS),
});
export const zkPinParams = z.object({ id: objectId, pin: zkPin });
// ─────────────────────────────────────────────────────────────────────────────
// Backup de sólo lectura del terminal (`viva-molinete backup`) → «Día de migración»
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Contenido del archivo que arma `viva-molinete backup` (ya descifrado). El archivo va
 * cifrado con una contraseña que tipea quien lo hace (formato en
 * `access-agent/src/zkteco/cifrado.ts`, `VIVABK1`); adentro va este JSON comprimido.
 *
 * Es un BACKUP: guarda todo lo que el equipo devolvió, aunque un PIN no sea de los que el
 * sistema usa. Qué entra al espejo lo decide el backend al importarlo (`zkDeviceUser`).
 */
export const ZK_TERMINAL_BACKUP_FORMAT = "viva-molinete-backup";
const backupPin = z.string().trim().min(1).max(24);
export const zkBackupUser = zkDeviceUser.extend({ pin: backupPin });
export const zkBackupTemplate = zkDeviceTemplate.extend({
    pin: backupPin,
    fid: z.number().int().min(0).max(255),
    template: z.string().min(1).max(40_000),
});
/** Una línea del attlog del pendrive: PIN y hora; el resto de las columnas, tal cual. */
export const zkBackupAttlogEntry = z.object({
    pin: backupPin,
    localTime: z.string().regex(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/),
    fields: z.array(z.string().max(40)).max(12),
});
export const ZK_PENDRIVE_FILE_KINDS = ["users", "templates", "attlog"];
/** Un archivo del pendrive del menú del terminal: siempre va crudo (base64) además de leído. */
export const zkBackupPendriveFile = z.object({
    name: z.string().max(300),
    kind: z.enum(ZK_PENDRIVE_FILE_KINDS),
    size: z.number().int().min(0),
    /** El formato que se reconoció, o `null` si no se reconoció (queda sólo crudo). */
    format: z.string().max(80).nullable(),
    /** Por qué no se pudo leer, o qué faltó para aprovecharlo. */
    note: z.string().max(500).nullable(),
    raw: z.string(),
});
export const zkTerminalBackupFile = z
    .object({
    format: z.literal(ZK_TERMINAL_BACKUP_FORMAT),
    version: z.literal(1),
    createdAt: z.string(),
    /** Lo leído por la red (SDK TCP). `null` si sólo se copió el pendrive. */
    terminal: z
        .object({
        ip: z.string().max(60),
        port: z.number().int(),
        info: zkDeviceInfo,
        users: z.array(zkBackupUser).max(50_000),
        templates: z.array(zkBackupTemplate).max(100_000),
        /** Total que el equipo estuvo deshabilitado durante la lectura, en ms. */
        disabledMs: z.number().int().min(0),
        /** En cuántos tramos (cada uno con su tope de seguridad). */
        disabledSpans: z.number().int().min(0),
    })
        .nullable(),
    /** Lo copiado del pendrive del menú del terminal. `null` si no se pasó carpeta. */
    pendrive: z
        .object({
        folder: z.string().max(500),
        files: z.array(zkBackupPendriveFile).max(20),
        users: z.array(zkBackupUser).max(50_000),
        templates: z.array(zkBackupTemplate).max(100_000),
        punches: z.array(zkBackupAttlogEntry).max(1_000_000),
    })
        .nullable(),
})
    .refine((file) => file.terminal !== null || file.pendrive !== null, {
    message: "El backup no tiene ni la lectura del equipo ni el pendrive",
});
/**
 * Tope del archivo `.vbk` que acepta el portal (en base64 son 60 MB, el máximo del cuerpo
 * de `import-backup`). El agente no genera uno más grande: no se podría subir.
 */
export const ZK_TERMINAL_BACKUP_MAX_BYTES = 45_000_000;
/**
 * `POST /api/migration/import-backup`: el archivo (base64) y la contraseña con la que se
 * hizo. El backend lo descifra en memoria; la contraseña no se guarda en ningún lado.
 */
export const zkTerminalBackupImportBody = z.object({
    zkDeviceId: objectId,
    /** El archivo `.vbk` en base64. ~60 MB de base64 ≈ 45 MB de archivo. */
    file: z
        .string()
        .min(1)
        .max(Math.ceil(ZK_TERMINAL_BACKUP_MAX_BYTES / 3) * 4),
    password: z.string().min(1).max(200),
});
//# sourceMappingURL=zkteco.js.map